import React, { useEffect, useMemo, useRef, useState } from 'react';
import HobtalkLoader from '../../components/common/HobtalkLoader';
import AccessibleModal from '../../components/common/AccessibleModal';
import { mediaStrings, moduleCommonStrings } from '../../content';
import { getPublicErrorMessage } from '../../utils/errors';
import { includesCollectionName, normalizeCollectionOptions } from '../../utils/collectionOptions';
import { FilePlus2, Info, X } from 'lucide-react';
import { accountStrings } from '../../content/account';
import RatingReadout from '../../components/common/RatingReadout';

/**
 * Retrieves file payload.
 *
 * @param {*} file - file value.
 * @returns {*} The derived operation result.
 */
function readFilePayload(file) {
  return new Promise((resolve, reject) => {
    const isZip = file.name.toLowerCase().endsWith('.zip');
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(mediaStrings.movies.import.fileReadError));
    reader.onload = () => {
      if (isZip) {
        const base64 = String(reader.result || '').split(',')[1] || '';
        resolve({ fileName: file.name, fileBase64: base64 });
      } else {
        resolve({ fileName: file.name, csvText: String(reader.result || '') });
      }
    };
    if (isZip) reader.readAsDataURL(file);
    else reader.readAsText(file);
  });
}

/**
 * Renders the letterboxd import modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.collections - collections value.
 * @param {*} props.error - Failure being normalized or reported.
 * @param {*} props.importResult - import result value.
 * @param {*} props.isImporting - Whether is importing is enabled.
 * @param {*} props.isPreviewing - Whether is previewing is enabled.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @param {*} props.onImport - Callback invoked when import occurs.
 * @param {*} props.onPreview - Callback invoked when preview occurs.
 * @param {*} props.preview - preview value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function LetterboxdImportModal({
  collections = [],
  error,
  importResult,
  isImporting,
  isPreviewing,
  onClose,
  onImport,
  onPreview,
  onUpgrade,
  preview,
  remainingEntries = null,
  usageReady = true,
  usageError = false,
  onRetryUsage,
}) {
  const inputRef = useRef(null);
  const collectionOptions = useMemo(() => normalizeCollectionOptions(collections), [collections]);
  const [filePayload, setFilePayload] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [collectionError, setCollectionError] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [importedRowIds, setImportedRowIds] = useState(new Set());
  const [visibleRowCount, setVisibleRowCount] = useState(50);
  const [collection, setCollection] = useState(collectionOptions[0] || 'My Movies');
  const [useNewCollection, setUseNewCollection] = useState(!collectionOptions.length);
  const visiblePreview = filePayload && !fileError ? preview : null;

  const importableRowIds = useMemo(
    () => (visiblePreview?.items || []).filter((item) => !item.duplicate && !importedRowIds.has(item.rowId)).map((item) => item.rowId),
    [visiblePreview, importedRowIds]
  );
  const selectedCount = selectedRowIds.size;
  const selectionLimit = usageReady ? Math.max(0, Math.min(visiblePreview?.maxBatchSize ?? 25, remainingEntries ?? Infinity)) : 0;
  const showProUpsell = usageReady && remainingEntries != null && remainingEntries < Math.min(importableRowIds.length, visiblePreview?.maxBatchSize ?? 25);
  const selectableRowIds = importableRowIds.slice(0, selectionLimit);
  const allImportableSelected = selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedRowIds.has(id));

  useEffect(() => {
    if (!usageReady) return;
    setSelectedRowIds((current) => current.size > selectionLimit ? new Set([...current].slice(0, selectionLimit)) : current);
  }, [selectionLimit, usageReady]);

  /**
   * Handles file change.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleFileChange = async (event) => {
    if (!usageReady) return;
    const [file] = Array.from(event.target.files || []);
    setFileError('');
    setFilePayload(null);
    setFileName('');
    setSelectedRowIds(new Set());
    setImportedRowIds(new Set());
    setVisibleRowCount(50);
    if (!file) return;
    if (!/\.(csv|zip)$/i.test(file.name)) {
      setFileError(mediaStrings.movies.import.invalidFile);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    try {
      const payload = await readFilePayload(file);
      const nextPreview = await onPreview(payload);
      if (!nextPreview) throw new Error(mediaStrings.movies.import.previewError);
      setFilePayload(payload);
      setFileName(file.name);
      setSelectedRowIds(new Set((nextPreview?.items || []).filter((item) => !item.duplicate)
        .slice(0, Math.min(nextPreview?.maxBatchSize ?? 25, remainingEntries ?? Infinity)).map((item) => item.rowId)));
    } catch (err) {
      setFileError(getPublicErrorMessage(err, mediaStrings.movies.import.previewError));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  /**
   * Handles import.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleImport = async () => {
    if (!usageReady || !filePayload || selectedCount === 0 || selectedCount > selectionLimit) return;
    if (useNewCollection && includesCollectionName(collectionOptions, collection)) {
      setCollectionError(mediaStrings.movies.form.errors.collectionDuplicate);
      return;
    }
    const imported = await onImport({
      ...filePayload,
      selectedRowIds: Array.from(selectedRowIds),
      collection: collection.trim() || 'My Movies',
    });
    if (imported) {
      const failed = new Set(imported.failures?.map((item) => item.rowId) || []);
      setImportedRowIds((current) => new Set([...current, ...[...selectedRowIds].filter((id) => !failed.has(id))]));
      setSelectedRowIds(new Set());
    }
  };

  /**
   * Performs the requested operation.
   *
   * @param {*} item - item value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggle = (item) => {
    if (item.duplicate || importedRowIds.has(item.rowId)) return;
    setSelectedRowIds((current) => {
      const next = new Set(current);
      if (next.has(item.rowId)) next.delete(item.rowId);
      else if (next.size < selectionLimit) next.add(item.rowId);
      return next;
    });
  };

  /**
   * Performs all.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggleAll = () => {
    setSelectedRowIds(allImportableSelected ? new Set() : new Set(selectableRowIds));
  };

  return (
    <AccessibleModal className="import-flow catalog-import-modal" overlayClassName="catalog-import-overlay" labelledBy="letterboxd-import-title" onClose={onClose}>
        {(isPreviewing || isImporting) && (
          <HobtalkLoader
            variant="overlay"
            label={isImporting ? mediaStrings.movies.import.loading.importing(selectedCount) : mediaStrings.movies.import.loading.previewing}
          />
        )}
        <header className="catalog-import-header">
          <h2 id="letterboxd-import-title">{mediaStrings.movies.import.title}</h2>
          <button type="button" className="btn-ghost icon-button" onClick={onClose} aria-label={moduleCommonStrings.actions.close} title={moduleCommonStrings.actions.close}><X size={18} /></button>
        </header>

        {!usageReady && <div className="catalog-import-usage-state" role={usageError ? 'alert' : 'status'}>
          <span>{usageError ? accountStrings.subscription.loadFailed : accountStrings.subscription.loading}</span>
          {usageError && <button type="button" className="inline-link" onClick={onRetryUsage}>{moduleCommonStrings.imports.retry}</button>}
        </div>}

        {!visiblePreview && <section className="catalog-import-section">
          <h3>{mediaStrings.movies.import.exportTitle}</h3>
          <p>{mediaStrings.movies.import.exportDetail}</p>
          <a className="inline-link" href="https://letterboxd.com/settings/data/" target="_blank" rel="noreferrer">{mediaStrings.movies.import.getExportAction}</a>
          <button type="button" className="catalog-file-picker" disabled={!usageReady} onClick={() => inputRef.current?.click()}>
            <FilePlus2 size={18} aria-hidden="true" />
            {fileName || mediaStrings.movies.import.filePicker}
          </button>
          <input ref={inputRef} type="file" accept=".csv,.zip,text/csv,application/zip" onChange={handleFileChange} hidden />
        </section>
        }
        {(fileError || error) && <div role="alert" className="catalog-import-error"><p className="error-copy">{fileError || getPublicErrorMessage(error)}</p><p>{moduleCommonStrings.imports.fileErrorHelp}</p></div>}
        {visiblePreview && showProUpsell && <div className="catalog-import-upsell">
          <div><strong>{moduleCommonStrings.imports.upsellTitle}</strong><p>{moduleCommonStrings.imports.upsellDetail}</p></div>
          <button type="button" onClick={onUpgrade}>{moduleCommonStrings.imports.moreWithPro}</button>
        </div>}
        {visiblePreview && (
          <section className="catalog-import-section catalog-review-section">
            <div className="catalog-review-heading">
              <h3>{mediaStrings.movies.import.reviewTitle}</h3>
              <details className="catalog-import-info"><summary aria-label="Import batch limit" title="Import batch limit"><Info size={16} /></summary><span>{moduleCommonStrings.imports.batchLimit(visiblePreview.maxBatchSize ?? 25)}</span></details>
              <button type="button" className="inline-link catalog-change-file" disabled={!usageReady} title={fileName} onClick={() => inputRef.current?.click()}>{moduleCommonStrings.imports.changeFile}</button>
              <input ref={inputRef} type="file" accept=".csv,.zip,text/csv,application/zip" onChange={handleFileChange} hidden />
            </div>
            <div className="catalog-import-metrics">
              <div><strong>{visiblePreview.totalRows}</strong><span>{mediaStrings.movies.import.rows}</span></div>
              <div><strong>{importableRowIds.length}</strong><span>{mediaStrings.movies.import.ready}</span></div>
              <div><strong>{visiblePreview.duplicateCount + importedRowIds.size}</strong><span>{mediaStrings.movies.import.duplicates}</span></div>
            </div>
            {visiblePreview.items.length === 0 && <p className="catalog-import-empty">{moduleCommonStrings.imports.noItems}</p>}
            {visiblePreview.items.length > 0 && importableRowIds.length === 0 && <p className="catalog-import-empty">{moduleCommonStrings.imports.allDuplicates}</p>}

            {importableRowIds.length > 0 && <>
            <div className="goodreads-collection-field">
              <label>
                <span>Watchlist</span>
                <div className="collection-picker">
                  {collectionOptions.length > 0 && (
                    <div className="collection-mode-toggle" role="group" aria-label="Watchlist mode">
                      <button
                        type="button"
                        className={!useNewCollection ? 'active' : ''}
                        onClick={() => {
                          setUseNewCollection(false);
                          setCollection(collectionOptions[0]);
                          setCollectionError('');
                        }}
                      >
                      {mediaStrings.movies.form.options.existing}
                      </button>
                      <button
                        type="button"
                        className={useNewCollection ? 'active' : ''}
                        onClick={() => {
                          setUseNewCollection(true);
                          setCollection('');
                          setCollectionError('');
                        }}
                      >
                      {mediaStrings.movies.form.options.createNew}
                      </button>
                    </div>
                  )}
                  {!useNewCollection && collectionOptions.length ? (
                    <select value={collection} onChange={(event) => setCollection(event.target.value)}>
                      {collectionOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  ) : (
                    <input value={collection} onChange={(event) => { setCollection(event.target.value); setCollectionError(''); }} placeholder={mediaStrings.movies.form.placeholders.collection} />
                  )}
                </div>
                {collectionError && <p className="field-error">{collectionError}</p>}
              </label>
            </div>

            <button type="button" className="catalog-selection-toggle" onClick={toggleAll} disabled={selectionLimit === 0}>
              <strong>{allImportableSelected ? mediaStrings.movies.import.clearSelection : mediaStrings.movies.import.selectImportable}</strong>
              <span>{mediaStrings.movies.import.selectedCount(selectedCount)}</span>
            </button>

            <div className="goodreads-import-list">
              {visiblePreview.items.slice(0, visibleRowCount).map((item) => (
                <label key={item.rowId} className={`goodreads-import-row ${item.duplicate || importedRowIds.has(item.rowId) ? 'duplicate' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedRowIds.has(item.rowId)}
                    disabled={item.duplicate || importedRowIds.has(item.rowId) || (!selectedRowIds.has(item.rowId) && selectedCount >= selectionLimit)}
                    onChange={() => toggle(item)}
                  />
                  <span>
                    <strong>{item.movie.title || mediaStrings.movies.import.untitledMovie}</strong>
                    <em>{[item.movie.releaseYear, item.movie.director].filter(Boolean).join(' • ')}</em>
                    {Number(item.movie.rating) > 0 && <RatingReadout rating={item.movie.rating} />}
                    {(item.duplicate || importedRowIds.has(item.rowId)) && <small>{item.duplicateReason || moduleCommonStrings.imports.alreadyImported}</small>}
                  </span>
                </label>
              ))}
            </div>
            {visiblePreview.items.length > visibleRowCount && <button type="button" className="btn-ghost compact-btn" onClick={() => setVisibleRowCount((count) => count + 50)}>{moduleCommonStrings.imports.showMore}</button>}
            </>}
          </section>
        )}

        {importResult && (
          <div className="import-result-card">
            <strong>{mediaStrings.movies.import.importComplete}</strong>
            <p>{mediaStrings.movies.import.result(importResult)}</p>
          </div>
        )}

        <div className="form-actions catalog-import-actions">
          <button type="button" className="btn-primary" onClick={handleImport} disabled={!usageReady || !filePayload || !visiblePreview || selectedCount === 0 || selectedCount > selectionLimit || isImporting || isPreviewing || Boolean(collectionError)}>
            {isImporting ? mediaStrings.movies.import.importing : mediaStrings.movies.import.importAction}
          </button>
        </div>
    </AccessibleModal>
  );
}
